import { get } from 'lodash';
import { useMemo } from 'react';
import type { Analysis } from 'venn-api';
import type { CustomizableMetric } from 'venn-utils';
import { formatExportableSubjectWithOptionalFee, formatSubjectWithOptionalFee } from '../../../legend';
import type { AnalysisRequest } from '../../../studio-blocks';
import type { Serie } from '../../../studio-blocks/components/charts/types';
import { convertToTimeseries } from '..';

interface UseTimeseriesDataProps {
  requests: AnalysisRequest[];
  analyses: (Analysis | undefined)[][];
  metric: CustomizableMetric | undefined;
  subjectColors: string[];
  addVenncastSeries: boolean | undefined;
  subjectHasProxy: { [subjectId: string]: boolean | undefined };
  proxySymbol: string;
}

export const useTimeseries = ({
  requests,
  analyses,
  metric,
  subjectColors,
  addVenncastSeries,
  subjectHasProxy,
  proxySymbol,
}: UseTimeseriesDataProps) => {
  const [series, exportableSeries] = useMemo(() => {
    const series: Serie[] = [];
    const exportableSeries: Serie[] = [];

    requests.forEach((req, index) => {
      const analysis = analyses?.[index]?.find((analysis) => analysis?.analysisType === metric?.analysisType);
      const venncastAnalysis =
        addVenncastSeries && analyses?.[index]?.find((analysis) => analysis?.analysisType === 'VENNCAST');
      if (!analysis || !metric) {
        return;
      }

      const analysisResult = get(analysis, metric.analysisResultKey!);
      const venncastResult = venncastAnalysis && get(venncastAnalysis, 'venncast');
      if (!analysisResult) {
        return;
      }

      const ts = metric.metricPath ? get(analysisResult[0], metric.metricPath) : analysisResult[0];
      if (!ts) {
        return;
      }

      const benchmarkName =
        req.relative && req.benchmark
          ? ` (Relative to: ${formatSubjectWithOptionalFee(req.benchmark, proxySymbol, !!subjectHasProxy[req.benchmark.id])})`
          : '';
      const name = `${formatSubjectWithOptionalFee(req.subject, proxySymbol, !!subjectHasProxy[req.subject.id])}${benchmarkName}`;
      const exportableName = `${formatExportableSubjectWithOptionalFee(req.subject)}${benchmarkName}`;
      const seriesData = convertToTimeseries(ts);

      const serie = {
        name,
        data: seriesData,
        color: subjectColors[index],
        ...(Array.isArray(venncastResult) && venncastResult[0] ? { venncast: venncastResult[0] } : undefined),
      };

      const exportableSerie = {
        name: exportableName,
        data: seriesData,
        ...(Array.isArray(venncastResult) && venncastResult[0] ? { venncast: venncastResult[0] } : undefined),
      };

      series.push(serie);

      // Prevent export return stream that could contain third-party data
      if (analysis.exportable?.[0]) {
        exportableSeries.push(exportableSerie);
      }
    });

    return [series, exportableSeries];
  }, [requests, analyses, addVenncastSeries, metric, proxySymbol, subjectHasProxy, subjectColors]);

  return { series, exportableSeries };
};
